import logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { InputWithError } from './InputWithError';

export const ForgotPasswordForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    onSubmit: async (values) => {
      setIsLoading(true);

      const toastId = toast.loading('Sending reset email...', {
        description: 'Please wait while we process your request',
      });

      try {
        const recaptchaToken = await executeRecaptcha?.('forgot_password');
        const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/forgot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: values.email,
            captchaToken: recaptchaToken,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to send reset email');
        }

        if (data.success) {
          setIsSubmitted(true);
          toast.success('Reset email sent!', {
            description: 'Check your email for password reset instructions',
            id: toastId,
          });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email';
        toast.error('Failed to send reset email', {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (isSubmitted) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className='overflow-hidden p-0'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <div className='p-6 md:p-8'>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col items-center text-center'>
                  <img src={logo} alt='Logo' className='size-16 mb-6' />
                  <h1 className='text-2xl font-bold'>Check your email</h1>
                  <p className='text-muted-foreground text-balance'>
                    We've sent password reset instructions to {formik.values.email}
                  </p>
                </div>
                <div className='flex flex-col gap-4'>
                  <p className='text-sm text-muted-foreground text-center'>
                    Didn't receive the email? Check your spam folder or try again.
                  </p>
                  <div className='flex flex-col gap-2'>
                    <Button
                      variant='outline'
                      className='w-full'
                      onClick={() => {
                        setIsSubmitted(false);
                        formik.resetForm();
                      }}>
                      Try again
                    </Button>
                    <Button asChild variant='link' className='w-full'>
                      <Link to='/auth/login'>Back to login</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className='bg-muted relative hidden md:block'>
              <img
                src='/placeholder.svg'
                alt='Image'
                className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2'>
          <form className='p-6 md:p-8' onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col items-center text-center'>
                <img src={logo} alt='Logo' className='size-16 mb-6' />
                <h1 className='text-2xl font-bold'>Forgot your password?</h1>
                <p className='text-muted-foreground text-balance'>
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>
              <InputWithError
                label='Email'
                id='email'
                name='email'
                type='email'
                placeholder='m@example.com'
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email ? formik.errors.email : undefined}
                touched={formik.touched.email}
                required
              />{' '}
              <div className='flex flex-col gap-2'>
                <Button type='submit' className='w-full' disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send reset link'}
                </Button>
                <div className='text-center'>
                  <Button asChild variant='link' className='text-sm'>
                    <Link to='/auth/login'>Back to login</Link>
                  </Button>
                </div>
              </div>
            </div>
          </form>
          <div className='bg-muted relative hidden md:block'>
            <img
              src='/placeholder.svg'
              alt='Image'
              className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
            />
          </div>
        </CardContent>
      </Card>
      <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
        By clicking continue, you agree to our <a href='#'>Terms of Service</a> and <a href='#'>Privacy Policy</a>.
      </div>
    </div>
  );
};
